import React, { useEffect, useMemo, useRef, useState } from 'react';
import { X, ChevronRight, Search } from 'lucide-react';
import ChatPanel from '../../common/ChatPanel';
import { useAuthStore } from '../../../store/authStore';
import { useInventoryStore, cataloguePickerItems } from '../../../store/inventoryStore';
import { useUsersStore } from '../../../store/usersStore';
import { useWarehouseStore } from '../../../store/warehouseStore';
import { useSitesStore } from '../../../store/sitesStore';
import {
  useStockRequestStore,
  StockRequest,
  StockRequestLine,
  formatRequestRef,
  effectiveLineItemId,
  fmtRequestDay,
} from '../../../store/stockRequestStore';
import {
  getRequestTheme,
  statusBadge,
  fmtStatusLabel,
  lineStatusBadgeClass,
} from './theme';
import LineActionsPanel from './LineActionsPanel';

interface Props {
  request: StockRequest;
  line: StockRequestLine;
  isDark: boolean;
  onClose: () => void;
  startReceive?: boolean;
}

const inputCls =
  'w-full text-sm rounded px-2.5 py-1.5 border focus:outline-none focus:ring-1 focus:ring-blue-500 box-border';

function fmtDate(d: Date | string | null | undefined): string {
  if (!d) return '—';
  const date = d instanceof Date ? d : new Date(d);
  if (isNaN(date.getTime())) return '—';
  return `${String(date.getDate()).padStart(2, '0')}/${String(date.getMonth() + 1).padStart(2, '0')}/${date.getFullYear()}`;
}

function fmtDateTime(d: Date): string {
  const date = d instanceof Date ? d : new Date(d);
  if (isNaN(date.getTime())) return '—';
  return `${fmtDate(date)} ${String(date.getHours()).padStart(2, '0')}:${String(date.getMinutes()).padStart(2, '0')}`;
}

const RequestLineDrawer: React.FC<Props> = ({
  request: initialRequest,
  line: initialLine,
  isDark,
  onClose,
  startReceive = false,
}) => {
  const theme = getRequestTheme(isDark);
  const { user } = useAuthStore();
  const { items } = useInventoryStore();
  const { users } = useUsersStore();
  const { warehouses, storageAreasByWarehouse } = useWarehouseStore();
  const { sites } = useSitesStore();
  const {
    requests,
    selectedRequest,
    loadRequestDetail,
    updateSheetLine,
    sendForRequesterReview,
    acceptSubstitute,
    declineSubstitute,
    counterPropose,
    error,
  } = useStockRequestStore();

  const [visible, setVisible] = useState(false);
  const [activityOpen, setActivityOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [localError, setLocalError] = useState('');
  const [proposeItemId, setProposeItemId] = useState('');
  const [showCounter, setShowCounter] = useState(false);
  const [counterItemId, setCounterItemId] = useState('');
  const [confirmDecline, setConfirmDecline] = useState(false);
  const [proposeTipOpen, setProposeTipOpen] = useState(false);
  const declineBtnRef = useRef<HTMLButtonElement>(null);
  const actionsRef = useRef<HTMLDivElement>(null);
  const [expandReceive, setExpandReceive] = useState(startReceive);

  useEffect(() => {
    requestAnimationFrame(() => setVisible(true));
  }, []);

  useEffect(() => {
    void loadRequestDetail(initialRequest.id);
  }, [initialRequest.id, loadRequestDetail]);

  useEffect(() => {
    if (!confirmDecline) return;
    const handlePointerDown = (e: MouseEvent | TouchEvent) => {
      const target = e.target as Node | null;
      if (target && declineBtnRef.current?.contains(target)) return;
      setConfirmDecline(false);
    };
    document.addEventListener('mousedown', handlePointerDown);
    document.addEventListener('touchstart', handlePointerDown);
    return () => {
      document.removeEventListener('mousedown', handlePointerDown);
      document.removeEventListener('touchstart', handlePointerDown);
    };
  }, [confirmDecline]);

  const close = () => {
    setVisible(false);
    setTimeout(onClose, 220);
  };

  // Prefer list data from fetchRequests so sheet/drawer edits show immediately;
  // selectedRequest (detail load) is a fallback for events/activity.
  const request = useMemo(() => {
    const byLine = requests.find(r => (r.lines ?? []).some(l => l.id === initialLine.id));
    const fromList = byLine ?? requests.find(r => r.id === initialRequest.id);
    if (fromList) {
      if (selectedRequest?.id === fromList.id && selectedRequest.events) {
        return { ...fromList, events: selectedRequest.events, lines: fromList.lines ?? selectedRequest.lines };
      }
      return fromList;
    }
    if (selectedRequest?.id === initialRequest.id) return selectedRequest;
    return initialRequest;
  }, [requests, selectedRequest, initialRequest, initialLine.id]);

  const line = useMemo(() => {
    const fromList = (request.lines ?? []).find(l => l.id === initialLine.id);
    return fromList ?? initialLine;
  }, [request.lines, initialLine]);

  useEffect(() => {
    setExpandReceive(startReceive);
    if (!startReceive) return;
    const t = window.setTimeout(() => {
      actionsRef.current?.scrollIntoView({ block: 'nearest' });
    }, 240);
    return () => window.clearTimeout(t);
  }, [startReceive, line.id]);

  const itemNameById = useMemo(() => {
    const map = new Map<string, string>();
    for (const item of items) map.set(item.id, item.name);
    return map;
  }, [items]);

  const originalName = line.lineType === 'free_text' && !line.itemId
    ? (line.freeTextDescription ?? 'Free text')
    : (line.itemId ? itemNameById.get(line.itemId) ?? 'Unknown item' : (line.freeTextDescription ?? 'Unknown item'));

  const effectiveId = effectiveLineItemId(line);
  const title = effectiveId
    ? (itemNameById.get(effectiveId) ?? originalName)
    : originalName;

  const substituteName = line.substituteItemId
    ? itemNameById.get(line.substituteItemId) ?? 'Unknown item'
    : null;

  const whName = warehouses.find(w => w.id === request.warehouseId)?.name ?? '—';
  const areaName = request.storageAreaId
    ? (storageAreasByWarehouse[request.warehouseId] ?? []).find(a => a.id === request.storageAreaId)?.name
    : null;
  const destination = areaName ? `${whName} / ${areaName}` : whName;
  const requesterName = users.find(u => u.id === request.requesterId)?.displayName ?? 'Unknown';
  const siteName = request.siteId
    ? sites.find(s => s.id === request.siteId)?.name ?? '—'
    : null;
  const jobTitle = request.jobId && request.siteId
    ? (sites.find(s => s.id === request.siteId)?.jobs ?? []).find(j => j.id === request.jobId)?.title ?? null
    : null;

  const lineEvents = useMemo(() => {
    const events = request.events ?? [];
    return events
      .filter(ev => ev.lineId === line.id)
      .slice()
      .sort((a, b) => b.createdAt.getTime() - a.createdAt.getTime());
  }, [request.events, line.id]);

  const isPendingNegotiation =
    line.substituteStatus === 'pending_requester'
    || line.substituteStatus === 'pending_manager';

  const lineTerminal = ['cancelled', 'unavailable', 'received'].includes(line.status);

  // Anyone who can open the drawer can respond when it's their turn
  // (includes solo users who are both requester and reviewer).
  const showRequesterNegotiation =
    line.substituteStatus === 'pending_requester'
    && !!user?.id
    && (request.requesterId === user.id || request.assignedTo === user.id);

  const showManagerNegotiation =
    line.substituteStatus === 'pending_manager'
    && !!user?.id
    && (
      request.reviewedBy === user.id
      || request.assignedTo === user.id
      || request.requesterId !== user.id
    );

  const showNegotiation = showRequesterNegotiation || showManagerNegotiation;

  // Propose / set substitute whenever the line isn't locked in a negotiation
  // or terminal state — not blocked when the current user is also the requester.
  const canEditSubstitute =
    !!user?.id
    && !isPendingNegotiation
    && !lineTerminal
    && line.substituteStatus !== 'accepted';

  const patchLine = async (patch: Parameters<typeof updateSheetLine>[2]) => {
    setLocalError('');
    setSaving(true);
    const ok = await updateSheetLine(request.id, line.id, patch);
    if (ok) await loadRequestDetail(request.id);
    setSaving(false);
    if (!ok) setLocalError(useStockRequestStore.getState().error ?? 'Update failed');
  };

  const refresh = async () => {
    await loadRequestDetail(request.id);
  };

  const handleApplySubstitute = async () => {
    const subId = proposeItemId || line.substituteItemId;
    if (!subId) return;
    setSaving(true);
    setLocalError('');
    // Same path as By Request: sets substitute, line → submitted,
    // substitute_status → pending_requester, assigns requester to review.
    const ok = await sendForRequesterReview(request.id, [{
      lineId: line.id,
      approvedQty: line.approvedQty ?? line.requestedQty,
      substituteItemId: subId,
    }]);
    setSaving(false);
    if (!ok) {
      setLocalError(useStockRequestStore.getState().error ?? 'Failed to send substitute for review');
      return;
    }
    setProposeItemId('');
    await refresh();
  };

  const handleClearSubstitute = async () => {
    setSaving(true);
    setLocalError('');
    const ok = await updateSheetLine(request.id, line.id, { substituteItemId: null });
    setSaving(false);
    if (!ok) setLocalError(useStockRequestStore.getState().error ?? 'Failed to clear substitute');
    else {
      setProposeItemId('');
      await refresh();
    }
  };

  const handleAccept = async () => {
    setSaving(true);
    setLocalError('');
    const ok = await acceptSubstitute(request.id, line.id);
    setSaving(false);
    if (!ok) setLocalError(useStockRequestStore.getState().error ?? 'Accept failed');
    else await refresh();
  };

  const handleDecline = async () => {
    setSaving(true);
    setLocalError('');
    const ok = await declineSubstitute(request.id, line.id);
    setSaving(false);
    if (!ok) setLocalError(useStockRequestStore.getState().error ?? 'Decline failed');
    else await refresh();
  };

  const handleCounter = async (substituteItemId: string) => {
    setSaving(true);
    setLocalError('');
    const ok = await counterPropose(request.id, line.id, {
      substituteItemId,
      approvedQty: line.approvedQty ?? line.requestedQty,
    });
    setSaving(false);
    if (!ok) setLocalError(useStockRequestStore.getState().error ?? 'Counter failed');
    else {
      setShowCounter(false);
      setCounterItemId('');
      await refresh();
    }
  };

  const Row: React.FC<{ label: string; children: React.ReactNode }> = ({ label, children }) => (
    <div className="flex items-center justify-between gap-4 py-2" style={{ borderBottom: `1px solid ${isDark ? '#2d3340' : '#f3f4f6'}` }}>
      <span className="text-sm font-medium flex-shrink-0" style={{ color: theme.text, minWidth: 110 }}>{label}</span>
      <span className="text-sm text-right min-w-0" style={{ color: theme.muted }}>{children}</span>
    </div>
  );

  const catalogOptions = cataloguePickerItems(items).filter(i => i.id !== line.itemId);

  return (
    <>
      <div
        className="fixed inset-0 z-40 transition-opacity duration-200"
        style={{ backgroundColor: 'rgba(0,0,0,0.4)', opacity: visible ? 1 : 0 }}
        onClick={close}
      />

      <div
        className="fixed top-0 right-0 h-full z-50 flex flex-col shadow-2xl"
        data-sheet-ignore-deselect
        style={{
          width: 400,
          backgroundColor: theme.cardBg,
          borderLeft: `1px solid ${theme.border}`,
          transform: visible ? 'translateX(0)' : 'translateX(100%)',
          transition: 'transform 220ms cubic-bezier(0.4,0,0.2,1)',
        }}
      >
        <div
          className="flex items-center justify-between px-5 py-3 flex-shrink-0"
          style={{ borderBottom: `1px solid ${theme.border}` }}
        >
          <div className="flex-1 min-w-0">
            <p className="text-[11px] uppercase tracking-wide" style={{ color: theme.muted }}>
              Request · {formatRequestRef(request.requestNumber)}
            </p>
            <h2 className="text-base font-semibold mt-1 truncate" style={{ color: theme.text }}>
              {title}
            </h2>
          </div>
          <button
            type="button"
            onClick={close}
            className="p-1.5 rounded hover:bg-gray-700/50 transition-colors ml-3 flex-shrink-0"
            style={{ color: theme.muted }}
          >
            <X size={16} />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto">
          <div ref={actionsRef} className="px-5 pt-4 pb-2">
            <p className="text-[11px] uppercase tracking-wide font-medium mb-2" style={{ color: theme.muted }}>
              Actions
            </p>
            <LineActionsPanel
              key={`${line.id}-${line.status}-${line.receivedQty}-${line.approvedQty}-${line.orderedQty}`}
              targets={[{ request, line }]}
              isDark={isDark}
              initialAction={expandReceive ? 'receive' : null}
              onComplete={() => {
                setExpandReceive(false);
                setLocalError('');
                void refresh();
              }}
            />
          </div>

          <div className="mx-5 my-2" style={{ height: 1, backgroundColor: theme.border }} />

          <div className="px-5 pt-2 pb-2">
            <p className="text-[11px] uppercase tracking-wide font-medium mb-1" style={{ color: theme.muted }}>
              Details
            </p>

            <Row label="Status">
              <span className={`${lineStatusBadgeClass} ${statusBadge(line.status, isDark)}`}>
                {fmtStatusLabel(line.status)}
              </span>
            </Row>

            <Row label="Item">
              <span style={{ color: theme.text }}>{originalName}</span>
            </Row>

            <Row label="Qty desired">{line.requestedQty}</Row>
            {line.approvedQty != null && <Row label="Approved">{line.approvedQty}</Row>}
            {line.orderedQty != null && <Row label="Ordered">{line.orderedQty}</Row>}
            <Row label="Received">{line.receivedQty}</Row>

            <Row label="Condition">
              <select
                value={line.condition}
                disabled={saving}
                onChange={e => void patchLine({ condition: e.target.value as 'new' | 'used' })}
                className="text-sm rounded px-2 py-0.5 border focus:outline-none"
                style={{ backgroundColor: theme.inputBg, borderColor: theme.border, color: theme.text }}
              >
                <option value="new">New</option>
                <option value="used">Used</option>
              </select>
            </Row>

            <Row label="Destination">{destination}</Row>
            <Row label="Requester">{requesterName}</Row>
            <Row label="Priority">
              <span className="capitalize">{request.priority}</span>
            </Row>

            {siteName && <Row label="Site">{siteName}</Row>}
            {jobTitle && <Row label="Job">{jobTitle}</Row>}

            <Row label="Supplier">{line.supplier || '—'}</Row>
            <Row label="ETA">{line.expectedDate ? fmtRequestDay(line.expectedDate) : '—'}</Row>
            <Row label="Date ordered">
              <span style={{ color: (line.orderedAt || request.orderedAt) ? theme.text : theme.muted }}>
                {line.orderedAt
                  ? fmtRequestDay(line.orderedAt)
                  : request.orderedAt
                    ? fmtRequestDay(request.orderedAt)
                    : '—'}
              </span>
            </Row>

            <Row label="PO ref">
              <input
                type="text"
                disabled={saving}
                defaultValue={line.poReference ?? ''}
                key={`${line.id}-po-${line.poReference ?? ''}`}
                onBlur={e => {
                  const v = e.target.value.trim() || null;
                  if (v === (line.poReference ?? null)) return;
                  void patchLine({ poReference: v });
                }}
                className="w-36 text-sm text-right rounded px-2 py-0.5 border focus:outline-none focus:ring-1 focus:ring-blue-500"
                style={{ backgroundColor: theme.inputBg, borderColor: theme.border, color: theme.text }}
              />
            </Row>

            {line.approvalReason && <Row label="Reason">{line.approvalReason}</Row>}
            {line.resolutionNotes && <Row label="Notes">{line.resolutionNotes}</Row>}
            {line.disputeReason && <Row label="Dispute">{line.disputeReason}</Row>}
            {request.notes && <Row label="Request notes">{request.notes}</Row>}
          </div>

          <div className="mx-5 my-2" style={{ height: 1, backgroundColor: theme.border }} />

          <div className="px-5 pb-2">
            <div className="flex items-center gap-1.5 mb-2">
              <p className="text-[11px] uppercase tracking-wide font-medium" style={{ color: theme.muted }}>
                Propose Substitute
              </p>
              <span
                className="relative inline-flex items-center"
                onMouseEnter={() => setProposeTipOpen(true)}
                onMouseLeave={() => setProposeTipOpen(false)}
              >
                <button
                  type="button"
                  className="p-0.5 rounded transition-colors"
                  style={{ color: isDark ? '#60a5fa' : '#3b82f6' }}
                  aria-label="What propose substitute means"
                  tabIndex={-1}
                >
                  <Search size={11} strokeWidth={2.5} />
                </button>
                {proposeTipOpen && (
                  <span
                    className="absolute left-1/2 -translate-x-1/2 bottom-full mb-1.5 z-20 pointer-events-none rounded-md px-2.5 py-1.5 text-[11px] leading-snug whitespace-normal"
                    style={{
                      width: 200,
                      backgroundColor: isDark ? '#1e2330' : '#1f2937',
                      color: '#e5e7eb',
                      border: `1px solid ${isDark ? '#3a4155' : '#374151'}`,
                      boxShadow: '0 4px 12px rgba(0,0,0,0.35)',
                    }}
                  >
                    Offer an alternative item. The requester reviews and accepts it before this line can continue.
                    <span
                      className="absolute left-1/2 -translate-x-1/2 top-full"
                      style={{
                        width: 0,
                        height: 0,
                        borderLeft: '5px solid transparent',
                        borderRight: '5px solid transparent',
                        borderTop: `5px solid ${isDark ? '#3a4155' : '#374151'}`,
                      }}
                    />
                  </span>
                )}
              </span>
            </div>

            {line.substituteItemId && substituteName && (
              <div className="text-sm mb-3" style={{ color: theme.text }}>
                <span style={{ color: theme.muted }}>{originalName}</span>
                {' → '}
                <span style={{ color: isDark ? '#4ade80' : '#16a34a', fontWeight: 500 }}>{substituteName}</span>
                {line.substituteStatus && (
                  <span className={`ml-2 ${lineStatusBadgeClass} ${statusBadge('substitute', isDark)}`}>
                    {fmtStatusLabel(line.substituteStatus)}
                  </span>
                )}
              </div>
            )}

            {showNegotiation && (
              <div className="space-y-2 mb-3">
                <div className="flex flex-wrap items-center gap-1.5">
                  <button
                    type="button"
                    onClick={() => { setConfirmDecline(false); void handleAccept(); }}
                    disabled={saving}
                    className="text-xs px-2.5 py-1.5 rounded bg-green-600 hover:bg-green-700 text-white font-medium disabled:opacity-50"
                  >
                    Accept
                  </button>
                  <button
                    type="button"
                    onClick={() => {
                      setConfirmDecline(false);
                      setShowCounter(v => !v);
                    }}
                    disabled={saving}
                    className="text-xs px-2.5 py-1.5 rounded font-medium disabled:opacity-50"
                    style={{ backgroundColor: isDark ? '#1e3a5f' : '#dbeafe', color: isDark ? '#93c5fd' : '#1d4ed8' }}
                  >
                    Change
                  </button>
                  <button
                    ref={declineBtnRef}
                    type="button"
                    onClick={() => {
                      if (confirmDecline) {
                        setConfirmDecline(false);
                        void handleDecline();
                      } else {
                        setShowCounter(false);
                        setConfirmDecline(true);
                      }
                    }}
                    disabled={saving}
                    className="text-xs px-2.5 py-1.5 rounded font-medium disabled:opacity-50"
                    style={
                      confirmDecline
                        ? { backgroundColor: isDark ? '#7f1d1d' : '#fecaca', color: isDark ? '#fca5a5' : '#b91c1c' }
                        : { backgroundColor: isDark ? '#2b3140' : '#e5e7eb', color: theme.muted }
                    }
                  >
                    {confirmDecline ? 'Sure?' : 'Decline'}
                  </button>
                </div>
                {showCounter && (
                  <div className="space-y-2">
                    <select
                      value={counterItemId}
                      onChange={e => setCounterItemId(e.target.value)}
                      className={inputCls}
                      style={{ backgroundColor: theme.inputBg, borderColor: theme.border, color: theme.text }}
                    >
                      <option value="">Select…</option>
                      {catalogOptions.map(i => (
                        <option key={i.id} value={i.id}>{i.name}</option>
                      ))}
                    </select>
                    <button
                      type="button"
                      disabled={saving || !counterItemId}
                      onClick={() => void handleCounter(counterItemId)}
                      className="h-8 px-3 rounded text-xs font-medium text-white bg-blue-600 hover:bg-blue-700 disabled:opacity-50"
                    >
                      Send
                    </button>
                  </div>
                )}
              </div>
            )}

            {canEditSubstitute && (
              <div className="space-y-2">
                <select
                  value={proposeItemId || line.substituteItemId || ''}
                  onChange={e => setProposeItemId(e.target.value)}
                  className={inputCls}
                  style={{ backgroundColor: theme.inputBg, borderColor: theme.border, color: theme.text }}
                >
                  <option value="">Select item…</option>
                  {catalogOptions.map(i => (
                    <option key={i.id} value={i.id}>{i.name}</option>
                  ))}
                </select>
                <div className="flex flex-wrap items-center gap-2">
                  <button
                    type="button"
                    disabled={saving || !proposeItemId}
                    onClick={() => void handleApplySubstitute()}
                    className="inline-flex items-center h-8 px-3 rounded text-xs font-medium text-white bg-blue-600 hover:bg-blue-700 disabled:opacity-50"
                  >
                    Submit substitute
                  </button>
                  {line.substituteItemId && !isPendingNegotiation && (
                    <button
                      type="button"
                      disabled={saving}
                      onClick={() => void handleClearSubstitute()}
                      className="inline-flex items-center h-8 px-3 rounded text-xs font-medium disabled:opacity-50"
                      style={{ color: isDark ? '#fca5a5' : '#b91c1c' }}
                    >
                      Clear
                    </button>
                  )}
                </div>
              </div>
            )}
          </div>

          <div className="mx-5 my-2" style={{ height: 1, backgroundColor: theme.border }} />

          <div className="px-5 pb-2">
            <p className="text-[11px] uppercase tracking-wide font-medium mb-2" style={{ color: theme.muted }}>
              Chat
            </p>
            <div style={{ height: 280 }}>
              <ChatPanel
                contextType="stock_request_line"
                contextId={line.id}
                isDark={isDark}
                theme={{
                  text: theme.text,
                  muted: theme.muted,
                  border: theme.border,
                  inputBg: theme.inputBg,
                  cardBg: theme.cardBg,
                }}
                emptyLabel="No messages on this line yet."
              />
            </div>
          </div>

          <div className="mx-5 my-2" style={{ height: 1, backgroundColor: theme.border }} />

          <div className="px-5 pb-5">
            <button
              type="button"
              onClick={() => setActivityOpen(o => !o)}
              className="flex items-center gap-1.5 w-full text-left mb-2 transition-colors"
              style={{ color: theme.muted }}
            >
              <ChevronRight
                size={14}
                style={{ transition: 'transform 180ms', transform: activityOpen ? 'rotate(90deg)' : 'rotate(0deg)' }}
              />
              <span className="text-[11px] uppercase tracking-wide font-medium">
                Activity {lineEvents.length > 0 && `(${lineEvents.length})`}
              </span>
            </button>

            {activityOpen && (
              lineEvents.length === 0 ? (
                <p className="text-sm" style={{ color: theme.muted }}>No activity on this line yet.</p>
              ) : (
                <div className="relative">
                  <div
                    className="absolute left-[6px] top-3 bottom-3 w-px"
                    style={{ backgroundColor: theme.border }}
                  />
                  <div className="flex flex-col">
                    {lineEvents.map(ev => (
                      <div key={ev.id} className="relative pl-5 pb-4">
                        <div
                          className="absolute left-0 top-[5px] w-3 h-3 rounded-full border-2"
                          style={{
                            backgroundColor: theme.cardBg,
                            borderColor: isDark ? '#60a5fa' : '#3b82f6',
                          }}
                        />
                        <p className="text-sm" style={{ color: theme.text }}>
                          {ev.description ?? ev.eventType}
                        </p>
                        <p className="text-xs mt-0.5" style={{ color: theme.muted }}>
                          {ev.userDisplayName} · {fmtDateTime(ev.createdAt)}
                        </p>
                      </div>
                    ))}
                  </div>
                </div>
              )
            )}
          </div>

          {(localError || error) && (
            <p className="px-5 pb-4 text-xs text-red-400">{localError || error}</p>
          )}
        </div>
      </div>
    </>
  );
};

export default RequestLineDrawer;
